import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Overline } from "@/components/motion";

const TOPICS = [
  { id: "space", label: "Space", heading: "Compact countertop footprint", details: "Fits on a standard kitchen counter, with an optional slim water tank or direct mains plumb-in. No dedicated room, no building works." },
  { id: "power", label: "Power & plumbing", heading: "Standard UK 13A plug", details: "No three-phase wiring or specialist electrical work. Plumb directly into your water supply or use the internal filtered reservoir — we handle setup either way." },
  { id: "hygiene", label: "Hygiene", heading: "Automated daily cleaning", details: "Automatic milk-system and group-head flush cycles keep the machine HACCP-friendly, and all servicing, maintenance and repairs are handled by us — wherever you're based. Your team just makes coffee." },
  { id: "contract", label: "Contract length", heading: "30-day rolling flexibility", details: "No three-year lock-ins. If your headcount changes or you move offices, pause or cancel with 30 days' notice. We collect the machine — that's it." },
];

export const Objections = () => {
  const [active, setActive] = useState(TOPICS[0]);

  return (
    <section id="objections" data-testid="objections-section" className="py-24 sm:py-32 bg-[#F7F5F0]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal><Overline>Objections, solved</Overline></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 font-display font-bold tracking-tight text-[#0B1E17] text-2xl sm:text-3xl lg:text-4xl max-w-2xl leading-tight">
            Built for modern facilities managers. No admin burden, no trap contracts.
          </h2>
        </Reveal>
        <div className="mt-12 grid lg:grid-cols-[280px_1fr] gap-8">
          <Reveal delay={0.12}>
            <div className="flex lg:flex-col gap-2 overflow-x-auto pb-2" role="tablist" data-testid="objections-tabs">
              {TOPICS.map((t) => (
                <button key={t.id} role="tab" aria-selected={active.id === t.id}
                  data-testid={`objection-tab-${t.id}`}
                  onClick={() => setActive(t)}
                  className={`text-left whitespace-nowrap lg:whitespace-normal rounded-xl px-5 py-4 text-sm font-bold transition-all duration-300 ${
                    active.id === t.id
                      ? "bg-[#0F2E23] text-[#F7F5F0]"
                      : "bg-white text-[#52665E] border border-[#E2DDD3] hover:border-[#1B5E46]/40"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="bg-white rounded-3xl border border-[#E2DDD3] p-8 sm:p-12 min-h-[240px]" data-testid="objection-panel">
              <AnimatePresence mode="wait">
                <motion.div key={active.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.35, ease: [0.215, 0.61, 0.355, 1] }}>
                  <p className="font-mono-brand text-xs font-semibold uppercase tracking-[0.22em] text-[#D97706]">{active.label}</p>
                  <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl text-[#0B1E17]">{active.heading}</h3>
                  <p className="mt-4 text-base text-[#52665E] leading-relaxed max-w-2xl">{active.details}</p>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
